import { AppEnum, ContactEnums, FormHeaderLink, OptionHelper, RecordMap } from "@/Shared";
import { UserModel } from '@/Domains';

import { ContactFormValues } from "../ContactFormValidation";
import { ContactModel } from '../ContactModel';
import { PersonFormViewModel } from "@/Domains/Person/Logic/ViewModels/PersonFormViewModel";


/**
 * Class: ContactFormViewModel
 *
 * Description:
 * ViewModel for managing contact form data and options.
 * Extends PersonFormViewModel to include person-related fields.
 * Responsibilities include:
 * - Providing form values for the UI
 * - Exposing dropdown options for contact types and referents
 * - Computing dynamic form title
 *
 * Author: Samano CASTRE
 * Date: July 17, 2025
 */
export class ContactFormViewModel extends PersonFormViewModel {

    private _headerLink? : FormHeaderLink;
    /**
     * =====================================================
     * Constructor
     * =====================================================
     *
     * @param viewEnums - Enum definitions used in the contact form
     * @param userMap - List of users for referent dropdown
     * @param contact - Optional existing contact for editing
     */
    public constructor(
        private viewEnums: ContactEnums,
        private userMap: RecordMap<UserModel>,
        private contact?: ContactModel,
    ) {
        super(viewEnums.CivilityEnum, contact?.person);
        this._headerLink = this.hasPhone() 
        ? {
            href  : 'tel:' + this.eitherPhone, 
            label : 'Tél : ' + this.eitherPhone,
            tooltip: 'Cliquer pour téléphoner le contact',
            className : 'title-phone'
        } : undefined
    }

    get headerLink() {
        return this._headerLink;
    }


    /**
     * =====================================================
     * Form Values
     * =====================================================
     *
     * Provides the values to populate the form fields.
     * Combines person fields (from parent) with contact-specific fields.
     */
    get formValues() {
        return {
            person: super.getFormValues(),
            contactType: this.contact?.contactType,
            note: this.contact?.note || "",
            origin: this.contact?.origin || "",
            agentId: String(this.contact?.agentId ?? ""),
            isClosed: !!this.contact?.closedAt
        } as ContactFormValues
    }

    /**
     * =====================================================
     * Options for Dropdowns
     * =====================================================
     *
     * contactTypeOptions - Returns sorted list of contact types
     * referentOptions - Returns list of users for referent selection
     */
    get contactTypeOptions(): AppEnum[] { 
        return OptionHelper.sortByLabel(this.viewEnums.ContactTypeEnum);
    }

    get referentOptions(): AppEnum[] {
        return OptionHelper.userOptions(this.userMap.toArray());
    }

     /**
     * =====================================================
     * Form Title
     * =====================================================
     *
     * title - Dynamic title based on whether editing or creating a contact
     */
    get title() {
        return this.contact ? this.fullName : "Création de contact";
    }
}